import classNames from 'classnames';
import parseISO from 'date-fns/parseISO';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { renderToString } from 'react-dom/server';
import { stringify } from 'qs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibleDate } from '../transaction-history-component-v2/AccessibleDate';
import { GenericComponent } from '../../components-tree';
import { InteractionDetailCell } from './InteractionDetailCell';
import { InteractionsListSkeleton, InteractionsListSkeletonRows } from './InteractionsListSkeleton';
import { ReactBootstrap } from '../../ReactBootstrap';
import { TranslationProvider } from '../TranslationProvider/TranslationProvider';
import { WidgetProps } from '../render-component';
import { queryClient } from '../../utils/query-client';

export const INTERACTIONS_LIST_CLASSNAME = 'qlfeditor-interactions-list';
const PAGE_SIZE = 10;

export interface InteractionsListAttributes {
  interactionIds: string[];
  showLabel?: boolean;
}

export interface PortalInteractionRow {
  id: string;
  eventTimestamp: string;
  label: string;
  status: string;
}

interface GetInteractionsPayload {
  data: PortalInteractionRow[];
  pageCount: number;
  isLast: boolean;
}

type ComponentDefinition = GenericComponent<InteractionsListAttributes>;
type BackendProps = WidgetProps<ComponentDefinition>;

interface Props {
  programId: string;
  attributes: ComponentDefinition['attributes'];
  id: string;
  interactionsFixture?: PortalInteractionRow[];
}

async function getInteractions(
  programId: string,
  interactionIds: string[],
  pageNumber: number,
): Promise<GetInteractionsPayload> {
  const query = stringify(
    { interactionIds, pageNumber, pageSize: PAGE_SIZE },
    { addQueryPrefix: true },
  );
  const response = await fetch(`/l/${programId}/api/interactions${query}`);
  return response.json();
}

export function serializeInteractionsListProps({ state, options }: BackendProps): Props {
  return {
    attributes: options.attributes,
    id: options.id,
    interactionsFixture: state.interactionsFixture,
    programId: state.programId,
  };
}

export function InteractionsListComponent(props: Props) {
  return (
    <TranslationProvider loader={<InteractionsListSkeleton />}>
      <QueryClientProvider client={queryClient}>
        <Component {...props} />
      </QueryClientProvider>
    </TranslationProvider>
  );
}

function Component({ attributes, id, interactionsFixture, programId }: Props) {
  const { t } = useTranslation('participant');
  const [currentPage, setCurrentPage] = useState(0);
  const interactionIds = attributes.interactionIds ?? [];
  const showLabel = !!attributes.showLabel;
  const columnCount = showLabel ? 4 : 3;

  const {
    data: interactionsData,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['interactions', programId, interactionIds, currentPage],
    queryFn: () => getInteractions(programId, interactionIds, currentPage),
    initialData: interactionsFixture
      ? { data: interactionsFixture, isLast: true, pageCount: 1 }
      : undefined,
    enabled: !interactionsFixture,
  });

  return (
    <div
      id={id}
      className={classNames(INTERACTIONS_LIST_CLASSNAME, attributes.class)}
      data-testid="interactions-list"
    >
      <table className="qlf-table">
        <caption className="sr-only">{t('aria.interactions.table.caption')}</caption>
        <thead>
          <tr>
            <th scope="col" className="date">
              <i className="fas fa-calendar" aria-hidden />
              <span style={{ marginLeft: 5 }}>
                {t('editor.interactions-list.table.columns.date')}
              </span>
            </th>
            {showLabel ? (
              <th scope="col" className="label">
                {t('editor.interactions-list.table.columns.label')}
              </th>
            ) : null}
            <th scope="col" className="status">
              {t('editor.interactions-list.table.columns.status')}
            </th>
            <th scope="col" className="receipt">
              <i className="fas fa-receipt" aria-hidden />
              <span className="sr-only">{t('aria.interactions.receipt')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {isPending ? (
            <InteractionsListSkeletonRows showLabel={showLabel} />
          ) : isError ? (
            <tr>
              <td colSpan={columnCount} role="alert">
                {t('aria.interactions.pageLoadError', { pageNumber: currentPage + 1 })}
              </td>
            </tr>
          ) : !interactionsData?.data.length ? (
            <tr>
              <td colSpan={columnCount}>
                <div style={{ textAlign: 'center' }}>
                  <p>{t('editor.interactions-list.list.empty')}</p>
                  <p aria-hidden>
                    <i className="fal fa-receipt fa-5x" style={{ marginBottom: 0 }} />
                  </p>
                </div>
              </td>
            </tr>
          ) : (
            interactionsData.data.map(interaction => (
              <tr key={interaction.id} data-type="item" data-id={interaction.id}>
                <td className="date">
                  <AccessibleDate date={parseISO(interaction.eventTimestamp)} />
                </td>
                {showLabel ? <td className="label">{interaction.label}</td> : null}
                <td className="status" data-status={interaction.status}>
                  {interaction.status}
                </td>
                <InteractionDetailCell
                  programId={programId}
                  interactionId={interaction.id}
                  isPreview={!!interactionsFixture}
                />
              </tr>
            ))
          )}
        </tbody>
        {interactionsData && interactionsData.pageCount > 1 ? (
          <tfoot>
            <tr>
              <td colSpan={columnCount}>
                <div className="pagination qlfeditor-pagination--section__cta">
                  <button
                    className="qlfeditor-pagination--button__cta"
                    onClick={() => setCurrentPage(page => page - 1)}
                    disabled={currentPage === 0 || isPending}
                    aria-label={t('aria.previousPage')}
                  >
                    <i className="fa fa-chevron-left" aria-hidden />
                  </button>
                  <div className="qlfeditor--pagination__cta">
                    {t('widgets.transactionHistory.pagination.position', {
                      current: currentPage + 1,
                      total: interactionsData.pageCount || 1,
                    })}
                  </div>
                  <button
                    className="qlfeditor-pagination--button__cta"
                    onClick={() => setCurrentPage(page => page + 1)}
                    disabled={interactionsData.isLast || isPending}
                    aria-label={t('aria.nextPage')}
                  >
                    <i className="fa fa-chevron-right" aria-hidden />
                  </button>
                </div>
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

export function InteractionsListRenderer(props: BackendProps, document?: Document) {
  const reactProps = serializeInteractionsListProps(props);
  return renderToString(
    <ReactBootstrap
      component="InteractionsListComponent"
      props={reactProps}
      document={document}
      componentType="interactions-list"
    />,
  );
}
